<script lang="ts">
  /**
   * Which seasons the draft spins through.
   *
   * The design draws a track with two handles and a lime fill between them.
   * This is built as two real `<input type="range">` elements stacked on that
   * track rather than as custom drag handles, because a range input already
   * carries keyboard support, arrow-key stepping, screen-reader semantics and
   * native touch behaviour — all of which would have to be rebuilt, badly, to
   * gain nothing but a tidier DOM.
   *
   * The consequence to manage is that two overlaid inputs both want the
   * pointer. Each is transparent and only its thumb accepts events, so the
   * track underneath stays visible and neither input swallows the other's
   * handle.
   */
  import type { Roster } from '../lib/roster';

  interface Props {
    roster: Roster;
    from: number;
    to: number;
    /** Applies the range and closes. */
    onconfirm: (from: number, to: number) => void;
  }
  let { roster, from, to, onconfirm }: Props = $props();

  /*
    These read their props once, on purpose, and Svelte's
    `state_referenced_locally` warning is about exactly that.

    It is right to warn in general and wrong here: the sheet is mounted only
    while it is open, `roster` is fixed for the life of the app, and `from`/`to`
    are a starting position for local state the user is about to change. Making
    them reactive would mean a re-render could yank the handles out from under a
    drag.
  */
  // svelte-ignore state_referenced_locally
  const years = roster.years;
  const first = years[0];
  const last = years[years.length - 1];

  // svelte-ignore state_referenced_locally
  let lo = $state(from);
  // svelte-ignore state_referenced_locally
  let hi = $state(to);

  // The two inputs are independent, so either can be dragged past the other.
  // Clamping on the way in keeps `lo <= hi` without the handles fighting.
  function setLo(v: number): void {
    lo = Math.min(v, hi);
  }
  function setHi(v: number): void {
    hi = Math.max(v, lo);
  }

  const span = $derived(last - first || 1);
  const leftPct = $derived(((lo - first) / span) * 100);
  const rightPct = $derived(((last - hi) / span) * 100);

  const seasons = $derived(hi - lo + 1);
  const squadCount = $derived(roster.squadsIn(lo, hi).length);
</script>

<div class="sheet open" aria-label="Choose seasons">
  <div class="scrim"></div>
  <div class="modal" role="dialog" aria-modal="true" aria-labelledby="seasons-title">
    <h2 id="seasons-title" style="text-align:center">Which seasons?</h2>
    <p class="muted" style="text-align:center">
      Squads from these years will be offered. Locked once you start.
    </p>

    <div class="years">
      <span class="year">{lo}</span><span class="faint">to</span><span class="year">{hi}</span>
    </div>

    <div class="track">
      <div class="fill" style="left:{leftPct}%; right:{rightPct}%"></div>
      <input
        class="thumb"
        type="range"
        min={first}
        max={last}
        step="1"
        value={lo}
        aria-label="First season"
        oninput={(e) => setLo(Number(e.currentTarget.value))}
      />
      <input
        class="thumb"
        type="range"
        min={first}
        max={last}
        step="1"
        value={hi}
        aria-label="Last season"
        oninput={(e) => setHi(Number(e.currentTarget.value))}
      />
    </div>

    <p class="faint" style="text-align:center;margin:var(--space-2) 0 0" aria-live="polite">
      {seasons} season{seasons === 1 ? '' : 's'} · {squadCount} squad{squadCount === 1 ? '' : 's'}
    </p>

    <button
      class="btn-primary"
      style="width:100%;margin-top:var(--space-5)"
      onclick={() => onconfirm(lo, hi)}
    >
      Start drafting
    </button>
  </div>
</div>

<style>
  /*
    The two inputs sit on top of the painted track. `pointer-events: none` on
    the input with `auto` on the thumb is what lets both handles be grabbed:
    without it the input rendered last would take every click across the whole
    width, and the lower handle would be unreachable.
  */
  .track {
    position: relative;
  }

  /*
    app.css draws decorative handles as .fill::before / ::after, for a mockup
    with no interaction behind it. The range thumbs below are the real ones and
    sit in the same places, so leaving these on renders two lime circles at each
    end of the fill.
  */
  .fill::before,
  .fill::after {
    display: none;
  }

  .thumb {
    position: absolute;
    left: 0;
    right: 0;
    top: 50%;
    transform: translateY(-50%);
    width: 100%;
    margin: 0;
    padding: 0;
    height: var(--tap);
    appearance: none;
    background: none;
    pointer-events: none;
  }

  .thumb:focus-visible {
    outline: none;
  }

  .thumb::-webkit-slider-thumb {
    pointer-events: auto;
    appearance: none;
    width: 24px;
    height: 24px;
    border-radius: 50%;
    background: var(--lime);
    border: 2px solid var(--bg);
    box-shadow: var(--glow-lime);
    cursor: grab;
  }

  .thumb::-moz-range-thumb {
    pointer-events: auto;
    width: 24px;
    height: 24px;
    border-radius: 50%;
    background: var(--lime);
    border: 2px solid var(--bg);
    box-shadow: var(--glow-lime);
    cursor: grab;
  }

  /* Focus has to be visible on the handle itself, since the input is invisible. */
  .thumb:focus-visible::-webkit-slider-thumb {
    outline: 3px solid var(--violet);
    outline-offset: 2px;
  }
  .thumb:focus-visible::-moz-range-thumb {
    outline: 3px solid var(--violet);
    outline-offset: 2px;
  }
</style>
